import { useEffect, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { anyApi } from "convex/server";
import { Dialog } from "./Dialog";
import { Field, inputCls, selectCls, textareaCls } from "./form-controls";

interface Unit {
  _id: string;
  label: string;
}

// Opens a fresh lease on a unit. By default closes any currently-
// active lease on the same unit (and ends its still-active tenancies)
// — there's only one active lease per unit at a time. Toggleable for
// the rare case where the landlord wants to layer.
export function StartLeaseDialog({
  open,
  onClose,
  propertySlug,
}: {
  open: boolean;
  onClose: () => void;
  propertySlug: string;
}) {
  const units = useQuery(
    anyApi.units.listForProperty,
    open ? { propertySlug } : "skip",
  ) as Unit[] | undefined;

  const [unitLabel, setUnitLabel] = useState("");
  const [startDate, setStartDate] = useState("");
  const [monthlyRent, setMonthlyRent] = useState("");
  const [notes, setNotes] = useState("");
  const [closeExisting, setCloseExisting] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setUnitLabel("");
    setStartDate(new Date().toISOString().slice(0, 10));
    setMonthlyRent("");
    setNotes("");
    setCloseExisting(true);
    setError(null);
  }, [open]);

  const submit = useMutation(anyApi.leases.startNew);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const cents = Math.round(Number(monthlyRent) * 100);
      if (!Number.isFinite(cents) || cents < 0) {
        throw new Error("monthly rent must be a non-negative number");
      }
      await submit({
        propertySlug,
        unitLabel,
        startDate,
        monthlyRentCents: cents,
        notes: notes || undefined,
        closeExistingActive: closeExisting,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Start a new lease">
      <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
        <Field label="Unit" required>
          <select
            value={unitLabel}
            onChange={(e) => setUnitLabel(e.target.value)}
            required
            className={selectCls}
          >
            <option value="">Pick a unit…</option>
            {(units ?? []).map((u) => (
              <option key={u._id} value={u.label}>
                {u.label}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Start date" required>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
              className={inputCls}
            />
          </Field>
          <Field label="Monthly rent (USD)" required>
            <input
              type="number"
              min="0"
              step="0.01"
              value={monthlyRent}
              onChange={(e) => setMonthlyRent(e.target.value)}
              required
              placeholder="1500"
              className={inputCls}
            />
          </Field>
        </div>
        <Field label="Notes (optional)">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Anything to remember about this contract"
            className={`${textareaCls} resize-none`}
          />
        </Field>
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={closeExisting}
            onChange={(e) => setCloseExisting(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            Close any active lease on this unit and end its tenants on the
            same date.{" "}
            <span className="text-slate-500">
              (Recommended — keeps lease docs isolated.)
            </span>
          </span>
        </label>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="submit"
            disabled={submitting || !unitLabel || !startDate || !monthlyRent}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            {submitting ? "Starting…" : "Start lease"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
